import { AlertTriangle, CheckCircle2, Layers, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EmptyState, ErrorState } from '@/components/states';
import { LinkRow } from '@/features/links/link-row';
import { useAdminFailuresQuery } from '@/hooks/use-admin-failures';
import { useCategoryLabels } from '@/hooks/use-categories';
import { pluralize } from '@/lib/format';
import type { AdminFailuresResponse, LinkSummary, QueueStats } from '@/types/api';

/** Work queues on the first row, their dead-letter queues beneath, in the same order. */
const QUEUE_LABELS: ReadonlyArray<{ key: keyof QueueMap; label: string; danger?: boolean }> = [
  { key: 'enrichment', label: 'Enrichment' },
  { key: 'screenshot', label: 'Screenshot' },
  { key: 'media', label: 'Media' },
  { key: 'mediaDescribe', label: 'Media titles' },
  { key: 'enrichmentDlq', label: 'Enrichment DLQ', danger: true },
  { key: 'screenshotDlq', label: 'Screenshot DLQ', danger: true },
  { key: 'mediaDlq', label: 'Media DLQ', danger: true },
  { key: 'mediaDescribeDlq', label: 'Media titles DLQ', danger: true },
];

type QueueMap = AdminFailuresResponse['queues'];

function QueueCard({
  label,
  stats,
  danger,
}: {
  label: string;
  stats: QueueStats;
  danger?: boolean;
}) {
  const total = stats.available + stats.inFlight;
  return (
    <Card className={danger && total > 0 ? 'border-destructive/50' : undefined}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Layers aria-hidden="true" className="h-4 w-4" />
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex items-baseline gap-4">
        <div>
          <p className="text-2xl font-semibold tabular-nums">{stats.available}</p>
          <p className="text-xs text-muted-foreground">Available</p>
        </div>
        <div>
          <p className="text-2xl font-semibold tabular-nums">{stats.inFlight}</p>
          <p className="text-xs text-muted-foreground">In flight</p>
        </div>
      </CardContent>
    </Card>
  );
}

function FailureList({
  links,
  emptyTitle,
  categoryLabels,
}: {
  links: LinkSummary[];
  emptyTitle: string;
  categoryLabels: Map<string, string>;
}) {
  if (links.length === 0) {
    return (
      <EmptyState
        icon={<CheckCircle2 aria-hidden="true" className="h-8 w-8 text-success" />}
        title={emptyTitle}
        description="Nothing needs attention right now."
      />
    );
  }
  return (
    <ul className="flex flex-col gap-3">
      {links.map((link) => (
        <li key={link.linkId}>
          <LinkRow link={link} categoryLabels={categoryLabels} />
        </li>
      ))}
    </ul>
  );
}

/** Admin operations view: what failed, and how deep the queues are. */
export function AdminFailuresPage() {
  const failures = useAdminFailuresQuery(true);
  const categoryLabels = useCategoryLabels();

  const enrichmentFailures = failures.data?.enrichmentFailures ?? [];
  const screenshotFailures = failures.data?.screenshotFailures ?? [];

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight">Failed jobs</h1>
          <p className="text-sm text-muted-foreground">
            Links whose enrichment or screenshot did not complete, plus current queue depth.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          loading={failures.isFetching}
          onClick={() => void failures.refetch()}
        >
          <RefreshCw aria-hidden="true" className="h-4 w-4" />
          Refresh
        </Button>
      </header>

      <p role="status" aria-live="polite" className="sr-only">
        {failures.isPending
          ? 'Loading failure report'
          : failures.isError
            ? 'Could not load the failure report.'
            : `${enrichmentFailures.length} enrichment ${pluralize(enrichmentFailures.length, 'failure')} and ${screenshotFailures.length} screenshot ${pluralize(screenshotFailures.length, 'failure')}.`}
      </p>

      {failures.isError ? (
        <ErrorState
          title="Could not load the failure report"
          error={failures.error}
          onRetry={() => void failures.refetch()}
        />
      ) : failures.isPending ? (
        <div className="flex flex-col gap-6" aria-hidden="true">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[0, 1, 2, 3, 4, 5, 6, 7].map((index) => (
              <Skeleton key={index} className="h-28 w-full" />
            ))}
          </div>
          <Skeleton className="h-10 w-64" />
          <div className="flex flex-col gap-3">
            {[0, 1, 2].map((index) => (
              <Skeleton key={index} className="h-28 w-full" />
            ))}
          </div>
        </div>
      ) : (
        <>
          <section aria-label="Queue statistics">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {QUEUE_LABELS.map(({ key, label, danger }) => {
                const stats = failures.data.queues[key];
                return stats ? (
                  <QueueCard key={key} label={label} danger={danger} stats={stats} />
                ) : null;
              })}
            </div>
          </section>

          <Tabs defaultValue="enrichment">
            <TabsList>
              <TabsTrigger value="enrichment">
                <AlertTriangle aria-hidden="true" className="h-4 w-4" />
                Enrichment ({enrichmentFailures.length})
              </TabsTrigger>
              <TabsTrigger value="screenshot">
                <AlertTriangle aria-hidden="true" className="h-4 w-4" />
                Screenshot ({screenshotFailures.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="enrichment">
              <FailureList
                links={enrichmentFailures}
                emptyTitle="No enrichment failures"
                categoryLabels={categoryLabels}
              />
            </TabsContent>

            <TabsContent value="screenshot">
              <FailureList
                links={screenshotFailures}
                emptyTitle="No screenshot failures"
                categoryLabels={categoryLabels}
              />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
